import React, {
  useEffect,
  useState,
} from "react";

import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";

import {
  ArrowLeft,
  Save,
} from "react-feather";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  toast,
} from "react-toastify";

import api from "../../../services/AxiosInstance";

import "../../../css/master-common.css";

// ============================================================
// INITIAL FORM
// ============================================================

const INITIAL_FORM = {
  // BASIC
  pan_id: "",
  dist_id: "",
  taluk_id: "",
  pan_name: "",
  status: 1,

  // CONTACT
  pdo_name: "",
  pdo_mobile_no: "",

  deo_name: "",
  deo_mobile_no: "",

  president_name: "",
  president_mobile_no: "",

  key_person_name: "",
  key_person_mobile_no: "",

  panchayath_email_id: "",

  // SUBSCRIPTION
  subscription_type: "",
  subscription_amount: "",
  tax_type: "",

  registration_date: "",
  bill_generation_date: "",
  billing_day: "",

  // EXECUTIVES
  sales_executive_id: "",
  support_executive_id: "",
};

// ============================================================
// HELPERS
// ============================================================

const cleanText = (
  value
) => {
  return String(
    value ?? ""
  ).trim();
};

const cleanMobile = (
  value
) => {
  return String(
    value ?? ""
  )
    .replace(
      /\D/g,
      ""
    )
    .slice(
      0,
      15
    );
};

const asDateInput = (
  value
) => {
  if (!value) {
    return "";
  }

  return String(
    value
  ).slice(
    0,
    10
  );
};


const getBillingDay = (
  value
) => {
  if (!value) {
    return "";
  }

  const date =
    String(
      value
    ).slice(
      0,
      10
    );

  const parts =
    date.split("-");

  if (
    parts.length !==
    3
  ) {
    return "";
  }

  const day =
    Number(
      parts[2]
    );

  return day >= 1 &&
    day <= 28
    ? String(day)
    : "";
};

const buildBillGenerationDate = (
  registrationDate,
  billingDay
) => {
  if (
    !registrationDate ||
    !billingDay
  ) {
    return "";
  }

  const [
    year,
    month,
    registrationDay,
  ] =
    String(
      registrationDate
    )
      .slice(
        0,
        10
      )
      .split("-")
      .map(Number);

  const day =
    Number(
      billingDay
    );

  if (
    !year ||
    !month ||
    !registrationDay ||
    day < 1 ||
    day > 28
  ) {
    return "";
  }

  let targetYear =
    year;

  let targetMonth =
    month;

  if (
    day <
    registrationDay
  ) {
    targetMonth++;

    if (
      targetMonth >
      12
    ) {
      targetMonth =
        1;

      targetYear++;
    }
  }

  return `${targetYear}-${String(
    targetMonth
  ).padStart(
    2,
    "0"
  )}-${String(
    day
  ).padStart(
    2,
    "0"
  )}`;
};

const FormSection = ({
  title,
  subtitle,
  children,
}) => {
  return (
    <Card className="mm-card mb-3">
      <Card.Body className="p-4">
        <div className="mb-4">
          <h5 className="fw-bold mb-1">
            {title}
          </h5>

          {subtitle ? (
            <div className="text-muted small">
              {subtitle}
            </div>
          ) : null}
        </div>

        {children}
      </Card.Body>
    </Card>
  );
};

// ============================================================
// REQUIRED LABEL
// ============================================================

const RequiredLabel = ({
  children,
}) => (
  <Form.Label className="fw-bold">
    {children}{" "}
    <span className="text-danger">
      *
    </span>
  </Form.Label>
);

// ============================================================
// PAGE
// ============================================================

export default function PanchayathMasterFormPage() {
  const navigate =
    useNavigate();

  const {
    pan_id,
  } =
    useParams();

  const isEdit =
    Boolean(
      pan_id
    );

  // ==========================================================
  // STATE
  // ==========================================================

  const [
    form,
    setForm,
  ] =
    useState({
      ...INITIAL_FORM,
    });

  const [
    districts,
    setDistricts,
  ] =
    useState([]);

  const [
    taluks,
    setTaluks,
  ] =
    useState([]);

  const [
    salesExecutives,
    setSalesExecutives,
  ] =
    useState([]);

  const [
    supportExecutives,
    setSupportExecutives,
  ] =
    useState([]);

  const [
    loading,
    setLoading,
  ] =
    useState(false);

  const [
    saving,
    setSaving,
  ] =
    useState(false);

  const [
    error,
    setError,
  ] =
    useState("");

  // ==========================================================
  // CHANGE
  // ==========================================================

  const change = (
    field,
    value
  ) => {
    setForm(
      (
        prev
      ) => ({
        ...prev,

        [field]:
          value,
      })
    );
  };

  // ==========================================================
  // DISTRICTS
  // ==========================================================

  const loadDistricts =
    async () => {
      try {
        const res =
          await api.get(
            "/api/panchayath/districts"
          );

        setDistricts(
          res.data?.rows ||
            res.data?.districts ||
            []
        );
      } catch (err) {
        console.error(
          "Load districts error:",
          err
        );

        setDistricts(
          []
        );
      }
    };

  // ==========================================================
  // TALUKS
  // ==========================================================

  const loadTaluks =
    async (
      distId
    ) => {
      if (!distId) {
        setTaluks(
          []
        );

        return;
      }

      try {
        const res =
          await api.get(
            "/api/panchayath/taluks",
            {
              params: {
                dist_id:
                  distId,
              },
            }
          );

        setTaluks(
          res.data?.rows ||
            res.data?.taluks ||
            []
        );
      } catch (err) {
        console.error(
          "Load taluks error:",
          err
        );

        setTaluks(
          []
        );
      }
    };

  // ==========================================================
  // SALES EXECUTIVES
  // ==========================================================

  const loadSalesExecutives =
    async () => {
      try {
        const res =
          await api.get(
            "/api/v1/sales-executives/lookup",
            {
              params: {
                status:
                  "Active",
              },
            }
          );

        setSalesExecutives(
          res.data?.data ||
            []
        );
      } catch (err) {
        console.error(
          "Sales executive lookup error:",
          err
        );

        setSalesExecutives(
          []
        );
      }
    };

  // ==========================================================
  // SUPPORT EXECUTIVES
  // ==========================================================

  const loadSupportExecutives =
    async () => {
      try {
        // Future module.
        setSupportExecutives(
          []
        );
      } catch (err) {
        setSupportExecutives(
          []
        );
      }
    };

  // ==========================================================
  // LOAD EDIT
  // ==========================================================

  const loadEditData =
    async () => {
      if (!pan_id) {
        return;
      }

      try {
        setLoading(
          true
        );

        setError(
          ""
        );

        const res =
          await api.get(
            `/api/v1/panchayath/panchayaths/${pan_id}`
          );

        if (
          res.data?.success ===
          false
        ) {
          throw new Error(
            res.data?.message ||
              "Failed to load Panchayath"
          );
        }

        const data =
          res.data?.data ||
          {};

        if (!data.pan_id) {
          throw new Error(
            "Panchayath not found"
          );
        }

        if (
          data.dist_id
        ) {
          await loadTaluks(
            data.dist_id
          );
        }

        setForm({
          ...INITIAL_FORM,

          pan_id:
            data.pan_id ||
            "",

          dist_id:
            data.dist_id ||
            "",

          taluk_id:
            data.taluk_id ||
            "",

          pan_name:
            data.pan_name ||
            "",

          status:
            Number(
              data.status
            ) === 0
              ? 0
              : 1,

          // CONTACT

          pdo_name:
            data.pdo_name ||
            "",

          pdo_mobile_no:
            data.pdo_mobile_no ||
            "",

          deo_name:
            data.deo_name ||
            "",

          deo_mobile_no:
            data.deo_mobile_no ||
            "",

          president_name:
            data.president_name ||
            "",

          president_mobile_no:
            data.president_mobile_no ||
            "",

          key_person_name:
            data.key_person_name ||
            "",

          key_person_mobile_no:
            data.key_person_mobile_no ||
            "",

          panchayath_email_id:
            data.panchayath_email_id ||
            "",

          // SUBSCRIPTION

          subscription_type:
            data.subscription_type ||
            "",

          subscription_amount:
            data.subscription_amount ??
            "",

          tax_type:
            data.tax_type ||
            "",

          registration_date:
            asDateInput(
              data.registration_date
            ),

          bill_generation_date:
            asDateInput(
              data.bill_generation_date
            ),

          billing_day:
            getBillingDay(
              data.bill_generation_date
            ),

          // EXECUTIVES

          sales_executive_id:
            data.sales_executive_id ||
            "",

          support_executive_id:
            data.support_executive_id ||
            "",
        });
      } catch (err) {
        console.error(
          "Load Panchayath error:",
          err
        );

        const message =
          err?.response?.data
            ?.message ||
          err?.message ||
          "Failed to load Panchayath";

        setError(
          message
        );

        toast.error(
          message
        );
      } finally {
        setLoading(
          false
        );
      }
    };

  // ==========================================================
  // INITIAL LOAD
  // ==========================================================

  useEffect(
    () => {
      loadDistricts();

      loadSalesExecutives();

      loadSupportExecutives();
    },
    []
  );

  useEffect(
    () => {
      if (isEdit) {
        loadEditData();
      } else {
        setForm({
          ...INITIAL_FORM,
        });

        setTaluks(
          []
        );
      }

      // eslint-disable-next-line
    },
    [
      pan_id,
    ]
  );

  // ==========================================================
  // DISTRICT CHANGE
  // ==========================================================

  const onDistrictChange =
    async (
      e
    ) => {
      const value =
        e.target.value;

      setForm(
        (
          prev
        ) => ({
          ...prev,

          dist_id:
            value,

          taluk_id:
            "",
        })
      );

      await loadTaluks(
        value
      );
    };

  // ==========================================================
  // VALIDATE
  // ==========================================================

  const validate =
    () => {
      // ======================================================
      // BASIC REQUIRED
      // ======================================================

      if (
        !form.dist_id
      ) {
        toast.warning(
          "Please select District"
        );

        return false;
      }

      if (
        !form.taluk_id
      ) {
        toast.warning(
          "Please select Taluk"
        );

        return false;
      }

      if (
        !cleanText(
          form.pan_name
        )
      ) {
        toast.warning(
          "Please enter Panchayath Name"
        );

        return false;
      }

      if (
        ![
          0,
          1,
        ].includes(
          Number(
            form.status
          )
        )
      ) {
        toast.warning(
          "Please select Status"
        );

        return false;
      }

      // ======================================================
      // SUBSCRIPTION REQUIRED
      // ======================================================

      if (
        !form.subscription_type
      ) {
        toast.warning(
          "Please select Subscription Type"
        );

        return false;
      }

      if (
        !form.subscription_amount ||
        Number(
          form.subscription_amount
        ) <= 0
      ) {
        toast.warning(
          "Please enter valid Subscription Amount"
        );

        return false;
      }

      if (
        !form.tax_type
      ) {
        toast.warning(
          "Please select Tax Type"
        );

        return false;
      }

      if (
        !form.registration_date
      ) {
        toast.warning(
          "Please select Registration Date"
        );

        return false;
      }

      if (
        !form.billing_day
      ) {
        toast.warning(
          "Please select Bill Generation Day"
        );

        return false;
      }

      const billingDay =
        Number(
          form.billing_day
        );

      if (
        billingDay < 1 ||
        billingDay > 28
      ) {
        toast.warning(
          "Bill Generation Day must be between 1 and 28"
        );

        return false;
      }

      // ======================================================
      // MOBILE
      // ======================================================

      const mobileFields = [
        {
          label:
            "PDO Mobile No.",
          value:
            form.pdo_mobile_no,
        },

        {
          label:
            "DEO Mobile No.",
          value:
            form.deo_mobile_no,
        },

        {
          label:
            "President Mobile No.",
          value:
            form.president_mobile_no,
        },

        {
          label:
            "Key Person Mobile No.",
          value:
            form.key_person_mobile_no,
        },
      ];

      for (
        const item of
        mobileFields
      ) {
        if (
          item.value &&
          !/^\d{10,15}$/.test(
            item.value
          )
        ) {
          toast.warning(
            `${item.label} should contain 10 to 15 digits`
          );

          return false;
        }
      }

      // ======================================================
      // EMAIL
      // ======================================================

      if (
        form.panchayath_email_id &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          cleanText(
            form.panchayath_email_id
          )
        )
      ) {
        toast.warning(
          "Please enter a valid Panchayath Email ID"
        );

        return false;
      }

      return true;
    };

  // ==========================================================
  // BUILD PAYLOAD
  // ==========================================================

  const buildPayload =
    () => {
      return {
        ...(isEdit
          ? {
              pan_id:
                Number(
                  pan_id
                ),
            }
          : {}),

        // BASIC

        dist_id:
          Number(
            form.dist_id
          ),

        taluk_id:
          Number(
            form.taluk_id
          ),

        pan_name:
          cleanText(
            form.pan_name
          ),

        status:
          Number(
            form.status
          ),

        // CONTACT

        pdo_name:
          cleanText(
            form.pdo_name
          ) ||
          null,

        pdo_mobile_no:
          cleanText(
            form.pdo_mobile_no
          ) ||
          null,

        deo_name:
          cleanText(
            form.deo_name
          ) ||
          null,

        deo_mobile_no:
          cleanText(
            form.deo_mobile_no
          ) ||
          null,

        president_name:
          cleanText(
            form.president_name
          ) ||
          null,

        president_mobile_no:
          cleanText(
            form.president_mobile_no
          ) ||
          null,

        key_person_name:
          cleanText(
            form.key_person_name
          ) ||
          null,

        key_person_mobile_no:
          cleanText(
            form.key_person_mobile_no
          ) ||
          null,

        panchayath_email_id:
          cleanText(
            form.panchayath_email_id
          ) ||
          null,

        // SUBSCRIPTION

        subscription_type:
          form.subscription_type,

        subscription_amount:
          Number(
            form.subscription_amount
          ),

        tax_type:
          form.tax_type,

        registration_date:
          form.registration_date,

        bill_generation_date:
          buildBillGenerationDate(
            form.registration_date,
            form.billing_day
          ),

        // EXECUTIVES

        sales_executive_id:
          form.sales_executive_id
            ? Number(
                form.sales_executive_id
              )
            : null,

        support_executive_id:
          form.support_executive_id
            ? Number(
                form.support_executive_id
              )
            : null,
      };
    };

  // ==========================================================
  // SAVE
  // ==========================================================

  const handleSave =
    async () => {
      if (
        !validate()
      ) {
        return;
      }

      try {
        setSaving(
          true
        );

        setError(
          ""
        );

        const payload =
          buildPayload();

        // ====================================================
        // EDIT
        // ====================================================

        if (isEdit) {
          const res =
            await api.put(
              `/api/v1/panchayath/panchayaths/${pan_id}`,
              payload
            );

          if (
            res.data?.success ===
            false
          ) {
            throw new Error(
              res.data?.message ||
                "Failed to update Panchayath"
            );
          }

          toast.success(
            "Panchayath updated successfully"
          );

          navigate(
            "/settings/panchayath-master"
          );

          return;
        }

        // ====================================================
        // CREATE
        // ====================================================

        const res =
          await api.post(
            "/api/v1/panchayath/panchayaths",
            payload
          );

        if (
          res.data?.success ===
          false
        ) {
          throw new Error(
            res.data?.message ||
              "Failed to create Panchayath"
          );
        }

        toast.success(
          "Panchayath created successfully"
        );

        navigate(
          "/settings/panchayath-master"
        );
      } catch (err) {
        console.error(
          "Panchayath save error:",
          err
        );

        const message =
          err?.response?.data
            ?.message ||
          err?.message ||
          "Failed to save Panchayath";

        setError(
          message
        );

        toast.error(
          message
        );
      } finally {
        setSaving(
          false
        );
      }
    };

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <div className="container-fluid px-3 py-4 mm-page">
        <Card className="mm-card">
          <Card.Body className="text-center py-5">
            <Spinner animation="border" />

            <div className="mt-3 text-muted">
              Loading Panchayath...
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // ==========================================================
  // UI
  // ==========================================================

  return (
    <div className="container-fluid px-3 py-4 mm-page">
      {/* ====================================================
          HEADER
      ==================================================== */}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mm-title mb-1">
            {isEdit
              ? "Edit Panchayath"
              : "Add Panchayath"}
          </h4>

          <div className="text-muted">
            {isEdit
              ? "Update Panchayath and subscription details"
              : "Create Panchayath with subscription details"}
          </div>
        </div>

        <Button
          variant="light"
          className="mm-cancel-btn d-flex align-items-center gap-2"
          onClick={() =>
            navigate(
              "/settings/panchayath-master"
            )
          }
          disabled={
            saving
          }
        >
          <ArrowLeft
            size={
              17
            }
          />

          Back
        </Button>
      </div>

      {/* ====================================================
          ERROR
      ==================================================== */}

      {error ? (
        <Alert
          variant="danger"
          className="mm-card-soft mb-3"
        >
          <div className="fw-bold">
            Error
          </div>

          <div>
            {error}
          </div>
        </Alert>
      ) : null}

      {/* ====================================================
          BASIC
      ==================================================== */}

      <FormSection
        title="Basic Details"
        subtitle="General Panchayath information"
      >
        <Row className="g-3">
          <Col
            lg={
              3
            }
            md={
              6
            }
          >
            <RequiredLabel>
              District
            </RequiredLabel>

            <Form.Select
              required
              className="mm-select"
              value={
                form.dist_id
              }
              onChange={
                onDistrictChange
              }
              disabled={
                saving
              }
            >
              <option value="">
                Select District
              </option>

              {districts.map(
                (
                  item
                ) => (
                  <option
                    key={
                      item.dist_id
                    }
                    value={
                      item.dist_id
                    }
                  >
                    {
                      item.dist_name
                    }
                  </option>
                )
              )}
            </Form.Select>
          </Col>

          <Col
            lg={
              3
            }
            md={
              6
            }
          >
            <RequiredLabel>
              Taluk
            </RequiredLabel>

            <Form.Select
              required
              className="mm-select"
              value={
                form.taluk_id
              }
              onChange={(e) =>
                change(
                  "taluk_id",
                  e.target.value
                )
              }
              disabled={
                !form.dist_id ||
                saving
              }
            >
              <option value="">
                Select Taluk
              </option>

              {taluks.map(
                (
                  item
                ) => (
                  <option
                    key={
                      item.taluk_id
                    }
                    value={
                      item.taluk_id
                    }
                  >
                    {
                      item.taluk_name
                    }
                  </option>
                )
              )}
            </Form.Select>
          </Col>

          <Col
            lg={
              3
            }
            md={
              6
            }
          >
            <RequiredLabel>
              Panchayath Name
            </RequiredLabel>

            <Form.Control
              required
              className="mm-input"
              value={
                form.pan_name
              }
              onChange={(e) =>
                change(
                  "pan_name",
                  e.target.value
                )
              }
              placeholder="Enter Panchayath Name"
              disabled={
                saving
              }
            />
          </Col>

          <Col
            lg={
              3
            }
            md={
              6
            }
          >
            <RequiredLabel>
              Status
            </RequiredLabel>

            <Form.Select
              required
              className="mm-select"
              value={
                form.status
              }
              onChange={(e) =>
                change(
                  "status",
                  Number(
                    e.target.value
                  )
                )
              }
              disabled={
                saving
              }
            >
              <option value={1}>
                Active
              </option>

              <option value={0}>
                Inactive
              </option>
            </Form.Select>
          </Col>
        </Row>
      </FormSection>

      {/* ====================================================
          CONTACT DETAILS
      ==================================================== */}

      <FormSection
        title="Contact Details"
        subtitle="Panchayath officials and key contact information"
      >
        <Row className="g-3">
          <Col md={6}>
            <Form.Label className="fw-bold">
              PDO Name
            </Form.Label>

            <Form.Control
              className="mm-input"
              value={
                form.pdo_name
              }
              onChange={(e) =>
                change(
                  "pdo_name",
                  e.target.value
                )
              }
              placeholder="Enter PDO name"
              disabled={
                saving
              }
            />
          </Col>

          <Col md={6}>
            <Form.Label className="fw-bold">
              PDO Mobile No.
            </Form.Label>

            <Form.Control
              className="mm-input"
              value={
                form.pdo_mobile_no
              }
              onChange={(e) =>
                change(
                  "pdo_mobile_no",
                  cleanMobile(
                    e.target.value
                  )
                )
              }
              maxLength={
                15
              }
              placeholder="Enter mobile number"
              disabled={
                saving
              }
            />
          </Col>

          <Col md={6}>
            <Form.Label className="fw-bold">
              DEO Name
            </Form.Label>

            <Form.Control
              className="mm-input"
              value={
                form.deo_name
              }
              onChange={(e) =>
                change(
                  "deo_name",
                  e.target.value
                )
              }
              placeholder="Enter DEO name"
              disabled={
                saving
              }
            />
          </Col>

          <Col md={6}>
            <Form.Label className="fw-bold">
              DEO Mobile No.
            </Form.Label>

            <Form.Control
              className="mm-input"
              value={
                form.deo_mobile_no
              }
              onChange={(e) =>
                change(
                  "deo_mobile_no",
                  cleanMobile(
                    e.target.value
                  )
                )
              }
              maxLength={
                15
              }
              placeholder="Enter mobile number"
              disabled={
                saving
              }
            />
          </Col>

          <Col md={6}>
            <Form.Label className="fw-bold">
              President Name
            </Form.Label>

            <Form.Control
              className="mm-input"
              value={
                form.president_name
              }
              onChange={(e) =>
                change(
                  "president_name",
                  e.target.value
                )
              }
              placeholder="Enter President name"
              disabled={
                saving
              }
            />
          </Col>

          <Col md={6}>
            <Form.Label className="fw-bold">
              President Mobile No.
            </Form.Label>

            <Form.Control
              className="mm-input"
              value={
                form.president_mobile_no
              }
              onChange={(e) =>
                change(
                  "president_mobile_no",
                  cleanMobile(
                    e.target.value
                  )
                )
              }
              maxLength={
                15
              }
              placeholder="Enter mobile number"
              disabled={
                saving
              }
            />
          </Col>

          <Col md={6}>
            <Form.Label className="fw-bold">
              Key Person Name
            </Form.Label>

            <Form.Control
              className="mm-input"
              value={
                form.key_person_name
              }
              onChange={(e) =>
                change(
                  "key_person_name",
                  e.target.value
                )
              }
              placeholder="Enter Key Person name"
              disabled={
                saving
              }
            />
          </Col>

          <Col md={6}>
            <Form.Label className="fw-bold">
              Key Person Mobile No.
            </Form.Label>

            <Form.Control
              className="mm-input"
              value={
                form.key_person_mobile_no
              }
              onChange={(e) =>
                change(
                  "key_person_mobile_no",
                  cleanMobile(
                    e.target.value
                  )
                )
              }
              maxLength={
                15
              }
              placeholder="Enter mobile number"
              disabled={
                saving
              }
            />
          </Col>

          <Col md={12}>
            <Form.Label className="fw-bold">
              Panchayath Email ID
            </Form.Label>

            <Form.Control
              type="email"
              className="mm-input"
              value={
                form.panchayath_email_id
              }
              onChange={(e) =>
                change(
                  "panchayath_email_id",
                  e.target.value
                )
              }
              placeholder="Example: office@panchayath.com"
              disabled={
                saving
              }
            />
          </Col>
        </Row>
      </FormSection>

      {/* ====================================================
          SUBSCRIPTION
      ==================================================== */}

      <FormSection
        title="Subscription & Billing Details"
        subtitle="Required configuration for automatic subscription billing"
      >
        <Row className="g-3">
          <Col
            lg={
              4
            }
            md={
              6
            }
          >
            <RequiredLabel>
              Subscription Type
            </RequiredLabel>

            <Form.Select
              required
              className="mm-select"
              value={
                form.subscription_type
              }
              onChange={(e) =>
                change(
                  "subscription_type",
                  e.target.value
                )
              }
              disabled={
                saving
              }
            >
              <option value="">
                Select Subscription
              </option>

              <option value="Monthly">
                Monthly
              </option>

              <option value="Quarterly">
                Quarterly
              </option>

              <option value="Half-Yearly">
                Half-Yearly
              </option>

              <option value="Yearly">
                Yearly
              </option>

              <option value="Lifetime">
                Lifetime
              </option>

              <option value="Custom">
                Custom
              </option>
            </Form.Select>
          </Col>

          <Col
            lg={
              4
            }
            md={
              6
            }
          >
            <RequiredLabel>
              Subscription Amount
            </RequiredLabel>

            <Form.Control
              required
              type="number"
              min="0.01"
              step="0.01"
              className="mm-input"
              value={
                form.subscription_amount
              }
              onChange={(e) =>
                change(
                  "subscription_amount",
                  e.target.value
                )
              }
              placeholder="0.00"
              disabled={
                saving
              }
            />
          </Col>

          <Col
            lg={
              4
            }
            md={
              6
            }
          >
            <RequiredLabel>
              Tax Type
            </RequiredLabel>

            <Form.Select
              required
              className="mm-select"
              value={
                form.tax_type
              }
              onChange={(e) =>
                change(
                  "tax_type",
                  e.target.value
                )
              }
              disabled={
                saving
              }
            >
              <option value="">
                Select Tax Type
              </option>

              <option value="GST">
                GST
              </option>

              <option value="Non-GST">
                Non-GST
              </option>
            </Form.Select>
          </Col>

          <Col
            lg={
              4
            }
            md={
              6
            }
          >
            <RequiredLabel>
              Registration Date
            </RequiredLabel>

            <Form.Control
              required
              type="date"
              className="mm-input"
              value={
                form.registration_date
              }
              onChange={(e) => {
                const value =
                  e.target.value;

                setForm(
                  (
                    prev
                  ) => ({
                    ...prev,

                    registration_date:
                      value,

                    billing_day:
                      value
                        ? prev.billing_day
                        : "",

                    bill_generation_date:
                      value &&
                      prev.billing_day
                        ? buildBillGenerationDate(
                            value,
                            prev.billing_day
                          )
                        : "",
                  })
                );
              }}
              disabled={
                saving
              }
            />

            <div className="text-muted small mt-1">
              Subscription active/start date.
            </div>
          </Col>

       <Col
  lg={4}
  md={6}
>
  <RequiredLabel>
    Bill Generation Day
  </RequiredLabel>

  <Form.Select
    required
    className="mm-select"
    value={
      form.billing_day
    }
    onChange={(e) => {
      const value =
        e.target.value;

      setForm(
        (prev) => ({
          ...prev,

          billing_day:
            value,

          bill_generation_date:
            prev.registration_date
              ? buildBillGenerationDate(
                  prev.registration_date,
                  value
                )
              : "",
        })
      );
    }}
    disabled={
      saving
    }
  >
    <option value="">
      Select Day
    </option>

    {Array.from(
      {
        length: 28,
      },
      (_, index) =>
        index + 1
    ).map(
      (day) => (
        <option
          key={day}
          value={day}
        >
          {day}
        </option>
      )
    )}
  </Form.Select>

  <div className="text-muted small mt-1">
    Select recurring billing day from 1 to 28.
  </div>
</Col>
        </Row>

        {/* <div className="mt-3 p-3 bg-light rounded">
          <div className="fw-bold mb-1">
            Billing Note
          </div>

          <div className="text-muted small">
            Installation, Training,
            Printer and Other charges
            are created separately from
            Panchayath Billing → Add Bill.
          </div>
        </div> */}
      </FormSection>

      {/* ====================================================
          SALES
      ==================================================== */}

      <FormSection
        title="Executive Assignment"
        subtitle="Sales and support executive information"
      >
        <Row className="g-3">
          <Col
            lg={
              6
            }
            md={
              6
            }
          >
            <Form.Label className="fw-bold">
              Sales Executive
            </Form.Label>

            <Form.Select
              className="mm-select"
              value={
                form.sales_executive_id
              }
              onChange={(e) =>
                change(
                  "sales_executive_id",
                  e.target.value
                )
              }
              disabled={
                saving
              }
            >
              <option value="">
                Select Sales Executive
              </option>

              {salesExecutives.map(
                (
                  item
                ) => (
                  <option
                    key={
                      item.sales_executive_id
                    }
                    value={
                      item.sales_executive_id
                    }
                  >
                    {item.sales_executive_emp_code
                      ? `${item.sales_executive_emp_code} - `
                      : ""}

                    {
                      item.sales_executive_name
                    }
                  </option>
                )
              )}
            </Form.Select>
          </Col>

          <Col
            lg={
              6
            }
            md={
              6
            }
          >
            <Form.Label className="fw-bold">
              Support Executive
            </Form.Label>

            <Form.Select
              className="mm-select"
              value={
                form.support_executive_id
              }
              onChange={(e) =>
                change(
                  "support_executive_id",
                  e.target.value
                )
              }
              disabled={
                saving ||
                supportExecutives.length ===
                  0
              }
            >
              <option value="">
                {supportExecutives.length
                  ? "Select Support Executive"
                  : "Support Executive master pending"}
              </option>

              {supportExecutives.map(
                (
                  item
                ) => (
                  <option
                    key={
                      item.support_executive_id
                    }
                    value={
                      item.support_executive_id
                    }
                  >
                    {
                      item.support_executive_name
                    }
                  </option>
                )
              )}
            </Form.Select>
          </Col>
        </Row>
      </FormSection>

      {/* ====================================================
          ACTIONS
      ==================================================== */}

      <Card className="mm-card mb-4">
        <Card.Body className="p-3">
          <div className="d-flex justify-content-end gap-2">
            <Button
              variant="outline-secondary"
              className="mm-cancel-btn"
              disabled={
                saving
              }
              onClick={() =>
                navigate(
                  "/settings/panchayath-master"
                )
              }
            >
              Cancel
            </Button>

            <Button
              className="mm-primary-btn d-flex align-items-center gap-2"
              disabled={
                saving
              }
              onClick={
                handleSave
              }
            >
              {saving ? (
                <>
                  <Spinner
                    size="sm"
                    animation="border"
                  />

                  Saving...
                </>
              ) : (
                <>
                  <Save
                    size={
                      17
                    }
                  />

                  {isEdit
                    ? "Update Panchayath"
                    : "Save Panchayath"}
                </>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}